import { useEffect, useContext, useState, useMemo } from 'react';
import { AuthContext } from '@/context';
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithRedirect,
  signInWithEmailLink,
  signOut,
  type User,
} from 'firebase/auth';
import { authorize, makePayloadRequestInit } from '@/lib/api';
import type { AuthorizationResponse } from '@/shared/types/auth';
import { SSR } from '@/env';
import { useFirebase } from '@/hooks/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfig } from './config';
import { generateNonce } from '@/lib/nonce';
import type { APIDispatch } from './api';
import { useApi } from './api';
import { clearActAsUserId } from '@/lib/actAs';
// import { initializeApp } from 'firebase/app';

export interface UserMetadata {
  id: string;
  data: Record<string, unknown>;
  unsignedAgreements: string[];
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useProvideAuth(): AuthContext {
  const appConfig = useConfig();
  const apiConfig = appConfig?.api || { baseUrl: '' };
  const [firebaseUser, setFirebaseUser] = useState<User | null | undefined>(
    undefined,
  );
  // const [errors, setErrors] = useState(null);
  const [authorized, setAuthorized] = useState<boolean | undefined>(undefined);
  const [authorizationErrorMessage, setAuthorizationErrorMessage] = useState<
    string | null
  >(null);
  const [ready, setReady] = useState(false);
  const [userMetadata, setUserMetadata] = useState<UserMetadata | null>(null);
  const [actorMetadata, setActorMetadata] = useState<UserMetadata | null>(null);
  const [actingAsUserId, setActingAsUserId] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);

  const firebase = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  // const { firebaseConfig } = useFirebase();

  // Install service worker so SSR requests are annotated with the Firebase ID token
  // useEffect(() => {
  //   async function installServiceWorker() {
  //     if ('serviceWorker' in navigator) {
  //       const serializedFirebaseConfig = encodeURIComponent(
  //         JSON.stringify(firebaseConfig),
  //       );
  //       const serviceWorkerUrl = `/serviceworker.js?firebaseConfig=${serializedFirebaseConfig}`;

  //       const registration =
  //         await navigator.serviceWorker.register(serviceWorkerUrl);
  //       console.log('scope is: ', registration.scope);
  //     }
  //   }

  //   // installServiceWorker();
  // }, [firebaseConfig]);

  // TODO @imcdonagh remove this if needed
  // useEffect(() => {
  //   debugger;
  //   if (!firebaseConfig) return;
  //   initializeApp(firebaseConfig);
  // }, [firebaseConfig]);

  // Hook into Firebase auth state changes
  useEffect(() => {
    if (SSR || !firebase) {
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      setFirebaseUser(user);
      // debugger;
      // setReady(true); // TODO:im
    });

    // unsubscribe on unmount so HMR doesn't end up with a huge stack of subscriptions
    return () => unsubscribe();
  });

  // Authorize when we get a Firebase user
  useEffect(() => {
    async function onAuthStateChanged(firebaseUser: User | null) {
      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        let authorization: AuthorizationResponse;
        try {
          authorization = await authorize({
            firebaseUser,
            options: { baseUrl: apiConfig.baseUrl },
          });
        } catch (e) {
          console.error(e);
          setAuthorized(false);
          setUserMetadata(null);
          setActorMetadata(null);
          setActingAsUserId(null);
          setAuthorizationErrorMessage(null);

          return;
        }

        setAuthorized(authorization.authorized);
        if (authorization.authorized) {
          setAuthorizationErrorMessage(null);
          const effectiveMetadata = {
            id: authorization.id,
            data: authorization.data,
            unsignedAgreements: authorization.unsignedAgreements,
          };
          setUserMetadata(effectiveMetadata);
          setActorMetadata(
            authorization.actor
              ? {
                  id: authorization.actor.id,
                  data: authorization.actor.data,
                  unsignedAgreements: [],
                }
              : effectiveMetadata,
          );
          setActingAsUserId(authorization.actingAs?.userId ?? null);
        } else {
          setUserMetadata(null);
          setActorMetadata(null);
          setActingAsUserId(null);
          setAuthorizationErrorMessage(authorization.message || null);
        }
      } else {
        setAuthorized(false);
        setUserMetadata(null);
        setActorMetadata(null);
        setActingAsUserId(null);
        setAuthorizationErrorMessage(null);
      }
    }

    if (firebaseUser !== undefined) {
      onAuthStateChanged(firebaseUser);
    }
  }, [firebaseUser, apiConfig.baseUrl]);

  // Get the ID token when the user changes
  useEffect(() => {
    async function onUserChange(user: User | null) {
      console.log('userchange:', user ? 'set' : 'null');
      if (!user) {
        setIdToken(null);

        return;
      }

      const token = await user.getIdToken();
      setIdToken(token);
      setReady(true);
    }

    if (firebaseUser !== undefined) {
      onUserChange(firebaseUser);
    }
  }, [firebaseUser]);

  // Redirects depending on final authorized state
  useEffect(() => {
    if (authorized) {
      if (location.pathname === '/login') {
        navigate('/');
      }
    } else if (authorized !== undefined) {
      if (location.pathname !== '/login') {
        navigate('/login');
      }
    }
  }, [authorized, navigate, location]);

  async function login() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    return signInWithRedirect(getAuth(), provider);
  }

  async function logout() {
    clearActAsUserId();
    await signOut(getAuth());
  }

  return {
    firebaseUser,
    login,
    logout,
    ready,
    authorized,
    authorizationErrorMessage,
    userMetadata,
    actorMetadata,
    actingAsUserId,
    idToken,
  };
}

export interface UserAccess {
  admin: boolean;
  people: boolean;
  workflows: boolean;
  communications: boolean;
}

export function useUserAccess(): UserAccess | null {
  const auth = useAuth();
  const meta = auth.userMetadata?.data;

  return useMemo(() => {
    if (!meta) {
      return null;
    }

    return {
      admin: Boolean(meta.admin) || false,
      people: Boolean(meta.people) || false,
      workflows: Boolean(meta.workflows) || false,
      communications: Boolean(meta.communications) || false,
    };
  }, [meta]);
}

export function useUserId() {
  const auth = useAuth();

  return auth.userMetadata?.id;
}

export function useEmailAuth() {
  const { api } = useApi();
  const [nonce, setNonce] = useState('');
  const initiateLogin = async (email: string) => {
    const nonce = generateNonce();
    setNonce(nonce);
    await emailLoginInitiate(api, nonce, email);
  };

  const verifyLogin = async (email: string, code: string) => {
    const res = await emailLoginVerify(api, email, code, nonce);

    return res;
  };

  return {
    initiateLogin,
    verifyLogin,
  };
}

async function emailLoginInitiate(
  api: APIDispatch,
  nonce: string,
  email: string,
) {
  const payload: EmailLoginInitiateRequest = {
    app: 'mhai',
    type: 'email',
    target: email,
    intent: 'login',
    nonce,
  };
  await api(
    '/auth/verification',
    null,
    makePayloadRequestInit(payload, 'POST'),
  );

  return { status: 'requested' };
}

async function emailLoginVerify(
  api: APIDispatch,
  email: string,
  code: string,
  nonce: string,
) {
  const payload: EmailLoginVerifyRequest = {
    type: 'email',
    target: email,
    code,
    nonce,
  };
  const res = await api(
    `/auth/verification`,
    null,
    makePayloadRequestInit(payload, 'PUT'),
  );
  const result = (await res.json()) as EmailLoginVerifyResponse;
  if (result.status === 'confirmed') {
    await emailLoginLink(api, email, result.verification_id);
  }

  return result;
}

type EmailLoginInitiateRequest = {
  app: 'mhai';
  type: 'email';
  target: string;
  intent: 'login';
  nonce: string;
};

type EmailLoginVerifyRequest = {
  type: 'email';
  target: string;
  code: string;
  nonce: string;
};

export type EmailLoginVerifyResponse =
  | {
      status: 'invalid';
    }
  | {
      status: 'expired' | 'used' | 'confirmed';
      verification_id: string;
    };

async function emailLoginLink(
  api: APIDispatch,
  email: string,
  verification_id: string,
) {
  const settings = {
    url: window.location.href,
    handleCodeInApp: true,
  };
  const payload: EmailLinkRequest = {
    email,
    verification_id,
    settings,
  };
  const res = await api(
    '/auth/emaillink',
    null,
    makePayloadRequestInit(payload, 'POST'),
  );
  const result = (await res.json()) as EmailLinkResponse;
  if (result.status === 'valid') {
    await signInWithEmailLink(getAuth(), email, result.link);
  }
}

type EmailLinkRequest = {
  email: string;
  verification_id: string;
  settings: {
    url: string;
    handleCodeInApp: boolean;
  };
};

type EmailLinkResponse =
  | {
      status: 'invalid' | 'expired';
    }
  | {
      status: 'valid';
      link: string;
    };
